import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtINR, fmtMeters, formatApiErrorDetail, fmtRate } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import {
  ArrowLeft, Truck, Wallet, Trash2, Plus, UploadCloud, Loader2, AlertTriangle, Calendar, FileText, Pencil, CheckCircle2, Ban,
} from "lucide-react";

const Pill = ({ status }) => {
  const map = {
    delivered: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
    partial: "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]",
    pending: "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]",
    paid: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
    unpaid: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
  };
  return <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border ${map[status] || ""}`}>{status}</span>;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const isAdmin = user?.role === "admin";
  const [o, setO] = useState(null);

  const load = () => api.get(`/orders/${id}`).then((r) => setO(r.data));
  useEffect(() => { load(); }, [id]);

  if (!o) return <div className="text-slate-400 font-mono text-sm">Loading order…</div>;

  const progress = o.quantity_meters > 0 ? Math.min(100, (o.delivered_quantity / o.quantity_meters) * 100) : 0;
  const payProgress = o.total_amount > 0 ? Math.min(100, (o.paid_amount / o.total_amount) * 100) : 0;

  const removeOrder = async () => {
    if (!window.confirm("Delete this order and all its deliveries & payments?")) return;
    await api.delete(`/orders/${id}`);
    toast.success("Order deleted.");
    navigate("/orders");
  };

  const toggleDelivered = async () => {
    const closing = !o.delivery_closed;
    try {
      await api.post(`/orders/${id}/close-delivery`, { delivery_closed: closing });
      toast.success(closing ? "Order marked as delivered — reminders will skip it." : "Delivery reopened.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed.");
    }
  };

  const showDeliveredToggle = o.delivery_closed || o.delivery_status !== "delivered";

  const togglePaymentClosed = async () => {
    const closing = !o.payment_closed;
    try {
      await api.post(`/orders/${id}/close-payment`, { payment_closed: closing });
      toast.success(closing ? "Marked fully received — reminders will skip it." : "Payment reopened.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed.");
    }
  };

  const toggleInvoicePaid = async (d) => {
    try {
      await api.post(`/orders/${id}/deliveries/${d.id}/paid`, { invoice_paid: !d.invoice_paid });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed.");
    }
  };

  const showPaymentToggle = o.payment_closed || o.payment_status !== "paid";

  const toggleCancel = async () => {
    const cancelling = !o.cancelled;
    try {
      await api.post(`/orders/${id}/cancel`, { cancelled: cancelling });
      toast.success(cancelling ? "Order cancelled — value set to zero." : "Order reopened — value restored.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed.");
    }
  };
  const invMap = Object.fromEntries((o.deliveries || []).map((d) => [d.id, d.challan_number || "Invoice"]));

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      <button onClick={() => navigate("/orders")} className="text-sm text-[#8CA3A7] flex items-center gap-1 hover:text-[#1C2331] transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to orders
      </button>

      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-mono text-sm text-[#D95D39]">{o.po_number || "No PO#"}</p>
            {o.overdue && (
              <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border bg-[#FEF2F2] text-[#991B1B] border-[#FECACA] flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Overdue
              </span>
            )}
            {o.cancelled && (
              <span data-testid="cancelled-badge" className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border bg-[#F3F4F6] text-[#4B5563] border-[#D1D5DB] line-through">Cancelled</span>
            )}
          </div>
          <h1 className="font-heading text-3xl font-bold text-[#0A0D14] tracking-tight mt-1">
            {o.buyer_name || "Unknown buyer"} <span className="text-[#8CA3A7] font-normal">from</span> {o.seller_name || "Unknown seller"}
          </h1>
          <p className="text-sm text-[#8CA3A7] mt-1">{o.fabric_type || "—"}{o.quality_details ? ` · ${o.quality_details}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (<>
          {showDeliveredToggle && (
            <button
              data-testid="mark-delivered-btn"
              onClick={toggleDelivered}
              className={`text-sm rounded-sm px-3 py-2 font-medium flex items-center gap-1.5 transition-colors border ${o.delivery_closed ? "bg-white text-[#4B5563] border-[#E6E4DD] hover:border-[#1C2331]" : "bg-[#065F46] text-white border-[#065F46] hover:bg-[#054d39]"}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> {o.delivery_closed ? "Reopen delivery" : "Mark delivered"}
            </button>
          )}
          {showPaymentToggle && (
            <button
              data-testid="mark-paid-btn"
              onClick={togglePaymentClosed}
              className={`text-sm rounded-sm px-3 py-2 font-medium flex items-center gap-1.5 transition-colors border ${o.payment_closed ? "bg-white text-[#4B5563] border-[#E6E4DD] hover:border-[#1C2331]" : "bg-[#1C2331] text-white border-[#1C2331] hover:bg-[#2A3447]"}`}
            >
              <Wallet className="w-3.5 h-3.5" /> {o.payment_closed ? "Reopen payment" : "Mark paid"}
            </button>
          )}
          <button
            data-testid="cancel-order-btn"
            onClick={toggleCancel}
            className={`text-sm rounded-sm px-3 py-2 font-medium flex items-center gap-1.5 transition-colors border ${o.cancelled ? "bg-white text-[#4B5563] border-[#E6E4DD] hover:border-[#1C2331]" : "bg-white text-[#991B1B] border-[#FECACA] hover:bg-red-50"}`}
          >
            <Ban className="w-3.5 h-3.5" /> {o.cancelled ? "Reopen order" : "Cancel order"}
          </button>
          <EditOrderDialog order={o} onDone={load} />
          {isAdmin && (
          <button data-testid="delete-order-btn" onClick={removeOrder} className="text-[#991B1B] hover:bg-red-50 p-2 rounded-sm transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
          )}
          </>)}
        </div>
      </div>

      {o.cancelled && (
        <div data-testid="cancelled-banner" className="flex items-center gap-2 rounded-sm border border-[#D1D5DB] bg-[#F3F4F6] px-4 py-3 text-sm text-[#4B5563]">
          <Ban className="w-4 h-4 shrink-0 text-[#991B1B]" />
          <span>This order is <strong>cancelled</strong> — its value, outstanding and commission are reset to ₹0. Reopen the order to restore the original amounts.</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-5">
          <span className="text-xs uppercase tracking-[0.1em] text-[#8CA3A7]">Order Value</span>
          <div className="font-mono text-xl tabular-nums text-[#0A0D14] mt-3">{fmtINR(o.total_amount)}</div>
          <div className="text-xs text-[#8CA3A7] font-mono mt-1">{fmtMeters(o.quantity_meters)} @ {fmtRate(o.rate_per_meter)}/m</div>
          {o.commission_amount > 0 && (
            <div className="text-xs text-[#065F46] font-mono mt-1" data-testid="order-commission">
              Commission {fmtINR(o.commission_amount)} <span className="text-[#8CA3A7]">({o.commission_type === "per_meter" ? `${fmtINR(o.commission_value)}/m` : `${o.commission_value}%`})</span>
            </div>
          )}
        </div>
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-5">
          <span className="text-xs uppercase tracking-[0.1em] text-[#8CA3A7]">Delivered</span>
          <div className="font-mono text-xl tabular-nums text-[#0A0D14] mt-3">{fmtMeters(o.delivered_quantity)}</div>
          <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#1C2331]" style={{ width: `${progress}%` }} /></div>
          <div className="mt-1"><Pill status={o.delivery_status} /></div>
          {o.delivery_closed && o.remaining_quantity > 0 && (
            <div data-testid="delivery-short-note" className="text-[10px] font-mono text-[#92400E] mt-1">
              Closed short · {fmtMeters(o.remaining_quantity)} undelivered
            </div>
          )}
        </div>
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-5">
          <span className="text-xs uppercase tracking-[0.1em] text-[#8CA3A7]">Paid</span>
          <div className="font-mono text-xl tabular-nums text-[#0A0D14] mt-3">{fmtINR(o.paid_amount)}</div>
          <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#065F46]" style={{ width: `${payProgress}%` }} /></div>
          <div className="mt-1"><Pill status={o.payment_status} /></div>
        </div>
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-5">
          <span className="text-xs uppercase tracking-[0.1em] text-[#8CA3A7]">Outstanding</span>
          <div className={`font-mono text-xl tabular-nums mt-3 ${o.outstanding_amount > 0 ? "text-[#D95D39]" : "text-[#065F46]"}`}>{fmtINR(o.outstanding_amount)}</div>
          <div className="text-xs text-[#8CA3A7] mt-1">
            {o.payment_terms || "No terms"}{o.due_date ? ` · due ${o.due_date}` : ""}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deliveries */}
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-lg font-semibold text-[#0A0D14] flex items-center gap-2"><Truck className="w-4 h-4 text-[#D95D39]" /> Deliveries</h2>
            {canEdit && <AddDeliveryDialog orderId={id} remaining={o.remaining_quantity} termsDays={o.payment_terms_days} onDone={load} />}
          </div>
          {o.deliveries.length === 0 ? (
            <div className="text-sm text-slate-400 font-mono py-6 text-center">No deliveries yet.</div>
          ) : (
            <div className="relative pl-4">
              <div className="absolute left-[5px] top-1 bottom-1 w-px bg-[#E6E4DD]" />
              <div className="space-y-4">
                {o.deliveries.map((d, i) => (
                  <div key={d.id} data-testid={`delivery-${d.id}`} className="relative fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                    <div className="absolute -left-4 top-1.5 w-2.5 h-2.5 rounded-full bg-[#D95D39] ring-4 ring-white" />
                    <div className="flex items-start justify-between border border-[#E6E4DD] rounded-sm p-3 ml-2">
                      <div className="flex gap-3">
                        {d.delivery_image ? (
                          <a href={d.delivery_image} target="_blank" rel="noreferrer" className="shrink-0">
                            <img src={d.delivery_image} alt="dispatch" className="w-14 h-14 object-cover rounded-sm border border-[#E6E4DD]" />
                          </a>
                        ) : null}
                        <div>
                          <div className="font-mono text-sm text-[#0A0D14]">{fmtMeters(d.quantity_meters)}</div>
                          <div className="text-xs text-[#8CA3A7] flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {d.delivery_date || "—"}</span>
                            {d.challan_number ? <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {d.challan_number}</span> : null}
                            {d.gr_number ? <span>GR: {d.gr_number}</span> : null}
                            {d.transport ? <span>{d.transport}</span> : null}
                            {d.mode ? <span>· {d.mode}</span> : null}
                          </div>
                          {d.payment_due_date ? <div className="text-xs text-[#D95D39] font-mono mt-0.5">Payment due: {d.payment_due_date}</div> : null}
                          <div className="text-[11px] font-mono mt-0.5">
                            <span className="text-[#8CA3A7]">Inv </span>{fmtINR(d.invoice_amount)}
                            {d.invoice_paid_amount > 0 ? <span className="text-[#065F46]"> · {fmtINR(d.invoice_paid_amount)} recd</span> : null}
                            {d.invoice_outstanding > 0 ? <span className="text-[#D95D39]"> · {fmtINR(d.invoice_outstanding)} due</span> : <span className="text-[#065F46]"> · settled</span>}
                          </div>
                          {d.notes ? <div className="text-xs text-[#4B5563] mt-1">{d.notes}</div> : null}
                        </div>
                      </div>
                      {canEdit && (
                      <div className="flex flex-col items-end gap-1.5">
                        <label className="flex items-center gap-1 text-[10px] text-[#4B5563] cursor-pointer select-none">
                          <input type="checkbox" data-testid={`invoice-paid-${d.id}`} checked={!!d.invoice_is_paid} onChange={() => toggleInvoicePaid(d)} className="accent-[#065F46] w-3.5 h-3.5" />
                          Paid
                        </label>
                        <div className="flex items-center gap-1">
                          <EditDeliveryDialog orderId={id} delivery={d} onDone={load} />
                          {isAdmin && (
                          <button
                            data-testid={`delete-delivery-${d.id}`}
                            onClick={async () => { await api.delete(`/orders/${id}/deliveries/${d.id}`); load(); }}
                            className="text-[#991B1B] hover:bg-red-50 p-1 rounded-sm"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          )}
                        </div>
                      </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Payments */}
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-lg font-semibold text-[#0A0D14] flex items-center gap-2"><Wallet className="w-4 h-4 text-[#D95D39]" /> Payments</h2>
            {canEdit && <AddPaymentDialog orderId={id} outstanding={o.outstanding_amount} deliveries={o.deliveries} onDone={load} />}
          </div>
          {o.payments.length === 0 ? (
            <div className="text-sm text-slate-400 font-mono py-6 text-center">No payments recorded.</div>
          ) : (
            <div className="space-y-3">
              {o.payments.map((p) => (
                <div key={p.id} data-testid={`payment-${p.id}`} className="flex items-start justify-between border border-[#E6E4DD] rounded-sm p-3">
                  <div>
                    <div className="font-mono text-sm text-[#065F46]">{fmtINR(p.amount)}</div>
                    <div className="text-xs text-[#8CA3A7] flex items-center gap-2 mt-1">
                      <Calendar className="w-3 h-3" /> {p.payment_date || "—"}
                      {p.reference ? <span>· {p.reference}</span> : null}
                      {p.delivery_id ? <span>· inv {invMap[p.delivery_id] || "—"}</span> : null}
                    </div>
                    {p.notes ? <div className="text-xs text-[#4B5563] mt-1">{p.notes}</div> : null}
                  </div>
                  {canEdit && (
                  <div className="flex items-center gap-1">
                    <EditPaymentDialog orderId={id} payment={p} deliveries={o.deliveries} onDone={load} />
                    {isAdmin && (
                    <button
                      data-testid={`delete-payment-${p.id}`}
                      onClick={async () => { await api.delete(`/orders/${id}/payments/${p.id}`); load(); }}
                      className="text-[#991B1B] hover:bg-red-50 p-1 rounded-sm"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    )}
                  </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {o.po_image ? (
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-6">
          <h2 className="font-heading text-lg font-semibold text-[#0A0D14] mb-4">Original Purchase Order</h2>
          <img src={o.po_image} alt="Purchase order" className="max-h-[500px] rounded-sm border border-[#E6E4DD]" />
        </div>
      ) : null}
    </div>
  );
}

function AddDeliveryDialog({ orderId, remaining, termsDays, onDone }) {
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState("");
  const [form, setForm] = useState({ delivery_date: today(), quantity_meters: "", amount: "", challan_number: "", gr_number: "", transport: "", mode: "", payment_due_date: "", notes: "", delivery_image: "" });
  const [saving, setSaving] = useState(false);

  // Auto-calc payment due date = dispatch date + credit days
  useEffect(() => {
    const days = Number(termsDays) || 0;
    if (days > 0 && form.delivery_date) {
      const d = new Date(form.delivery_date);
      d.setDate(d.getDate() + days);
      const due = d.toISOString().slice(0, 10);
      setForm((f) => (f.payment_due_date === due ? f : { ...f, payment_due_date: due }));
    }
  }, [form.delivery_date, termsDays]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setScanning(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/extract/delivery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        delivery_date: data.delivery_date || f.delivery_date,
        quantity_meters: data.quantity_meters || f.quantity_meters,
        amount: data.amount || f.amount,
        challan_number: data.challan_number || f.challan_number,
        gr_number: data.gr_number || f.gr_number,
        transport: data.transport || f.transport,
        mode: data.mode || f.mode,
        payment_due_date: data.payment_due_date || f.payment_due_date,
        delivery_image: data.delivery_image || "",
      }));
      toast.success("Delivery details extracted — review & save.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read image. Enter manually.");
    } finally {
      setScanning(false);
    }
  };

  const save = async () => {
    if (!form.quantity_meters) { toast.error("Enter delivered quantity."); return; }
    setSaving(true);
    try {
      await api.post(`/orders/${orderId}/deliveries`, { ...form, quantity_meters: Number(form.quantity_meters) || 0, amount: Number(form.amount) || 0 });
      toast.success("Delivery added.");
      setOpen(false);
      setPreview(""); setForm({ delivery_date: today(), quantity_meters: "", amount: "", challan_number: "", gr_number: "", transport: "", mode: "", payment_due_date: "", notes: "", delivery_image: "" });
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-delivery-btn" className="text-sm bg-white border border-[#E6E4DD] rounded-sm px-3 py-1.5 font-medium flex items-center gap-1.5 hover:border-[#1C2331] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add batch
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Add Delivery Batch</DialogTitle><DialogDescription className="sr-only">Add a delivery batch for this order</DialogDescription></DialogHeader>
        <p className="text-xs text-[#8CA3A7] -mt-2">Remaining to deliver: {fmtMeters(remaining)}. Upload a challan photo or enter manually.</p>

        <div
          data-testid="delivery-upload-zone"
          onClick={() => fileRef.current?.click()}
          className="relative overflow-hidden border-2 border-dashed border-[#E6E4DD] bg-slate-50/50 rounded-sm p-4 text-center cursor-pointer hover:bg-slate-50 transition-colors"
        >
          {preview ? (
            <div className="relative">
              <img src={preview} alt="challan" className="max-h-40 mx-auto object-contain" />
              {scanning && <div className="scanline" />}
              {scanning && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#D95D39]" /></div>}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-3 text-[#8CA3A7]">
              <UploadCloud className="w-5 h-5 text-[#D95D39]" />
              <span className="text-xs">Upload delivery challan photo</span>
            </div>
          )}
        </div>
        <input ref={fileRef} data-testid="delivery-file-input" type="file" accept="image/*" className="hidden" onChange={onFile} />

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Quantity (m)</label>
            <input data-testid="delivery-qty" type="number" value={form.quantity_meters} onChange={(e) => setForm({ ...form, quantity_meters: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Invoice Amount (₹) — grand total incl. GST</label>
            <input data-testid="delivery-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Auto-read from invoice; leave 0 to auto-calc" className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Dispatch Date</label>
            <input data-testid="delivery-date" type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Dispatch #</label>
            <input data-testid="delivery-challan" value={form.challan_number} onChange={(e) => setForm({ ...form, challan_number: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">GR Number</label>
            <input data-testid="delivery-gr" value={form.gr_number} onChange={(e) => setForm({ ...form, gr_number: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Payment Due On</label>
            <input data-testid="delivery-due" type="date" value={form.payment_due_date} onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Transport</label>
            <input data-testid="delivery-transport" value={form.transport} onChange={(e) => setForm({ ...form, transport: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Mode</label>
            <input data-testid="delivery-mode" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Notes</label>
            <input data-testid="delivery-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
        </div>

        <button data-testid="save-delivery-btn" onClick={save} disabled={saving} className="w-full bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save Delivery"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function AddPaymentDialog({ orderId, outstanding, deliveries, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ payment_date: today(), amount: "", reference: "", notes: "", delivery_id: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.amount) { toast.error("Enter payment amount."); return; }
    setSaving(true);
    try {
      await api.post(`/orders/${orderId}/payments`, { ...form, amount: Number(form.amount) || 0 });
      toast.success("Payment recorded.");
      setOpen(false);
      setForm({ payment_date: today(), amount: "", reference: "", notes: "", delivery_id: "" });
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-payment-btn" className="text-sm bg-white border border-[#E6E4DD] rounded-sm px-3 py-1.5 font-medium flex items-center gap-1.5 hover:border-[#1C2331] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Record payment
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Record Payment</DialogTitle><DialogDescription className="sr-only">Record a payment for this order</DialogDescription></DialogHeader>
        <p className="text-xs text-[#8CA3A7] -mt-2">Outstanding: {fmtINR(outstanding)}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Against Invoice</label>
            <select data-testid="payment-invoice" value={form.delivery_id} onChange={(e) => {
              const did = e.target.value;
              const inv = (deliveries || []).find((x) => x.id === did);
              setForm((f) => ({ ...f, delivery_id: did, amount: inv && !f.amount ? String(Math.round(inv.invoice_outstanding || 0)) : f.amount }));
            }} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
              <option value="">— Order level (no specific invoice) —</option>
              {(deliveries || []).map((d) => (
                <option key={d.id} value={d.id}>{`${d.challan_number || "Invoice"} · ${d.delivery_date || ""} · ${fmtINR(d.invoice_outstanding)} due`}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Amount (₹)</label>
            <input data-testid="payment-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Date</label>
            <input data-testid="payment-date" type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Reference</label>
            <input data-testid="payment-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Notes</label>
            <input data-testid="payment-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
        </div>
        <button data-testid="save-payment-btn" onClick={save} disabled={saving} className="w-full bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save Payment"}
        </button>
      </DialogContent>
    </Dialog>
  );
}


const EDIT_FIELDS = [
  ["po_number", "Buyer P.O. Number"],
  ["po_date", "P.O. Date"],
  ["buyer_name", "Buyer"],
  ["seller_name", "Seller"],
  ["fabric_type", "Fabric"],
  ["quality_details", "Quality"],
  ["quantity_meters", "Quantity (m)"],
  ["rate_per_meter", "Rate/m (₹)"],
  ["total_amount", "Amount (₹)"],
  ["expected_delivery_date", "Delivery Date"],
  ["payment_terms", "Payment Terms"],
  ["payment_terms_days", "Credit Days"],
];

function EditOrderDialog({ order, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(order);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(order); }, [order]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        po_number: form.po_number || "", po_date: form.po_date || "",
        buyer_name: form.buyer_name || "", seller_name: form.seller_name || "",
        category_seller: form.category_seller || "",
        fabric_type: form.fabric_type || "", quality_details: form.quality_details || "",
        quantity_meters: Number(form.quantity_meters) || 0, rate_per_meter: Number(form.rate_per_meter) || 0,
        total_amount: Number(form.total_amount) || 0, payment_terms: form.payment_terms || "",
        expected_delivery_date: form.expected_delivery_date || "",
        payment_terms_days: Number(form.payment_terms_days) || 0, notes: form.notes || "",
        commission_type: form.commission_type || "percent",
        commission_value: Number(form.commission_value) || 0,
      };
      await api.put(`/orders/${order.id}`, payload);
      toast.success("Order updated.");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="edit-order-btn" className="text-sm bg-white border border-[#E6E4DD] rounded-sm px-3 py-2 font-medium flex items-center gap-1.5 hover:border-[#1C2331] transition-colors">
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Edit Order Details</DialogTitle><DialogDescription className="sr-only">Edit the details of this order</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          {EDIT_FIELDS.map(([key, label]) => (
            <div key={key} className={key === "payment_terms" || key === "quality_details" ? "col-span-2" : ""}>
              <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">{label}</label>
              <input
                data-testid={`edit-field-${key}`}
                type={["quantity_meters", "rate_per_meter", "total_amount", "payment_terms_days"].includes(key) ? "number" : "text"}
                value={form[key] ?? ""}
                onChange={(e) => set(key, e.target.value)}
                className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]"
              />
            </div>
          ))}
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Notes</label>
            <input data-testid="edit-field-notes" value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Commission Type</label>
            <select data-testid="edit-field-commission_type" value={form.commission_type ?? "percent"} onChange={(e) => set("commission_type", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
              <option value="percent">% of order value</option>
              <option value="per_meter">₹ per meter</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">{form.commission_type === "per_meter" ? "Commission ₹/meter" : "Commission %"}</label>
            <input data-testid="edit-field-commission_value" type="number" value={form.commission_value ?? 0} onChange={(e) => set("commission_value", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
        </div>
        <button data-testid="save-edit-order-btn" onClick={save} disabled={saving} className="w-full bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </DialogContent>
    </Dialog>
  );
}



function EditDeliveryDialog({ orderId, delivery, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(delivery);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(delivery); }, [delivery]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.quantity_meters) { toast.error("Enter delivered quantity."); return; }
    setSaving(true);
    try {
      await api.put(`/orders/${orderId}/deliveries/${delivery.id}`, {
        delivery_date: form.delivery_date || "", quantity_meters: Number(form.quantity_meters) || 0,
        amount: Number(form.amount) || 0, challan_number: form.challan_number || "",
        gr_number: form.gr_number || "", transport: form.transport || "", mode: form.mode || "",
        payment_due_date: form.payment_due_date || "", notes: form.notes || "",
      });
      toast.success("Delivery updated.");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`edit-delivery-${delivery.id}`} className="text-[#4B5563] hover:bg-slate-100 p-1 rounded-sm">
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Edit Delivery Batch</DialogTitle><DialogDescription className="sr-only">Edit this delivery batch</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Quantity (m)</label>
            <input data-testid="edit-delivery-qty" type="number" value={form.quantity_meters ?? ""} onChange={(e) => set("quantity_meters", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Invoice Amount (₹) — grand total incl. GST</label>
            <input data-testid="edit-delivery-amount" type="number" value={form.amount ?? ""} onChange={(e) => set("amount", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Dispatch Date</label>
            <input data-testid="edit-delivery-date" type="date" value={form.delivery_date ?? ""} onChange={(e) => set("delivery_date", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Dispatch #</label>
            <input data-testid="edit-delivery-challan" value={form.challan_number ?? ""} onChange={(e) => set("challan_number", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">GR Number</label>
            <input data-testid="edit-delivery-gr" value={form.gr_number ?? ""} onChange={(e) => set("gr_number", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Payment Due On</label>
            <input data-testid="edit-delivery-due" type="date" value={form.payment_due_date ?? ""} onChange={(e) => set("payment_due_date", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Transport</label>
            <input data-testid="edit-delivery-transport" value={form.transport ?? ""} onChange={(e) => set("transport", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Mode</label>
            <input data-testid="edit-delivery-mode" value={form.mode ?? ""} onChange={(e) => set("mode", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Notes</label>
            <input data-testid="edit-delivery-notes" value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
        </div>
        <button data-testid={`save-edit-delivery-${delivery.id}`} onClick={save} disabled={saving} className="w-full bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function EditPaymentDialog({ orderId, payment, deliveries, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(payment);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(payment); }, [payment]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.amount) { toast.error("Enter payment amount."); return; }
    setSaving(true);
    try {
      await api.put(`/orders/${orderId}/payments/${payment.id}`, {
        payment_date: form.payment_date || "", amount: Number(form.amount) || 0,
        reference: form.reference || "", notes: form.notes || "", delivery_id: form.delivery_id || "",
      });
      toast.success("Payment updated.");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`edit-payment-${payment.id}`} className="text-[#4B5563] hover:bg-slate-100 p-1 rounded-sm">
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Edit Payment</DialogTitle><DialogDescription className="sr-only">Edit this payment</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Against Invoice</label>
            <select data-testid="edit-payment-invoice" value={form.delivery_id ?? ""} onChange={(e) => set("delivery_id", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
              <option value="">— Order level (no specific invoice) —</option>
              {(deliveries || []).map((d) => (
                <option key={d.id} value={d.id}>{`${d.challan_number || "Invoice"} · ${d.delivery_date || ""} · ${fmtINR(d.invoice_outstanding)} due`}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Amount (₹)</label>
            <input data-testid="edit-payment-amount" type="number" value={form.amount ?? ""} onChange={(e) => set("amount", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Date</label>
            <input data-testid="edit-payment-date" type="date" value={form.payment_date ?? ""} onChange={(e) => set("payment_date", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Reference</label>
            <input data-testid="edit-payment-reference" value={form.reference ?? ""} onChange={(e) => set("reference", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Notes</label>
            <input data-testid="edit-payment-notes" value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
        </div>
        <button data-testid={`save-edit-payment-${payment.id}`} onClick={save} disabled={saving} className="w-full bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
